/* eslint-disable max-lines */
import * as React from 'react';

import { OfferContainer } from '../../../../fragments/OfferCardWidget/shared/containers/OfferContainer';
import { isNewbuildingBookedFromDeveloper } from '../../../../fragments/OfferCardWidget/shared/utils/newbuildingBooking';
import { OneTwoTripBannerContainer } from '../../../containers/OneTwoTripBannerContainer';
import { IJsonQuery, IJsonQueryUnderground } from '../../../json_query';
import { IBreadcrumb, IOffer } from '../../../offer/types';
import { trackHiddenObjects } from '../../../tracking/hide_offer';
import { IAdfoxPuidMap } from '../../../types/adfox';
import { EDealType } from '../../../types/dealType';
import { dealTypeFromJsonQuery } from '../../../utils/category';
import { isDealRentDisablePhonesExperimentActive } from '../../../utils/experiments/dealRentDisablePhones';
import { isRosreestrCheckedLabelAvailable } from '../../../utils/isRosreestrCheckedLabelAvailable';
import { isSaleFromSuburbanBuilder } from '../../../utils/isSaleFromSuburbanBuilder';
import { IMakeRequest } from '../../../utils/request';
import { shouldSendAdsEvent } from '../../../utils/shouldSendAdsEvent';
import { getOfferKpAdvantages } from '../../../utils/trackings';
import { BannerConatiner } from '../../containers/Banner';
import { EHideOfferState } from '../../state/hide_offer';
import { CountrysideTrapBanner } from '../CountrysideTrapBanner';
import { addAdFoxBanner } from '../adfox';
import { TOffersProps } from './index';
import { prepareOfferCardComponentsProps } from './offer_card';
import { trackFavoriteAdded, trackFavoriteRemoved, trackPageJumped, trackPhoneOpened } from './tracking';

type TSuggestionFragment = { isSimilar: false } | { isSimilar: true; startIndex: number };
export interface IIntegrateAdOptions {
  currentPageNumber: number;
  perPage: number;
  jsonQuery: IJsonQuery;
  offers: IOffer[];
  offerCardCreator(
    offer: IOffer,
    position: number,
    index: number,
    currentPageNumber: number,
    keyPrefix: string,
  ): React.ReactNode;
  isPrintEnabled: boolean;
  suggestionFragment: TSuggestionFragment;
  makeRequest: IMakeRequest;
  hiddenOffers?: number[];
  hiddenSuggestions?: number[];
  animatingOffer?: number | null;
  locationId?: number;
  positionOffset?: number;
  extendedOffersOffset?: number;
  priceRange?: string;
  mlRankingGuid: string | null;
  mlRankingModelVersion: string | null;
  isAgent?: boolean;
  villagesLink?: string;
  showCountrysideTrap?: boolean;
  keyPrefix: string;
  isQaMode?: boolean;
  trackShowNBMotivationPopup?: boolean;
  motivationPopupPosition?: number;
  realtyUserId?: number | null;
  isCountrysideBuildersBannerAvailable?: boolean;
  isFilterByFlatsEnabled?: boolean;
  isNewSimplifiedTGBEnabled: boolean;
  puidsByFilter?: IAdfoxPuidMap | null;
}

export const integrateAd = (options: IIntegrateAdOptions) => {
  const {
    currentPageNumber,
    perPage,
    offers,
    offerCardCreator,
    isPrintEnabled,
    makeRequest,
    locationId,
    suggestionFragment,
    jsonQuery,
    priceRange,
    hiddenOffers = [],
    hiddenSuggestions = [],
    isAgent,
    villagesLink,
    showCountrysideTrap,
    extendedOffersOffset,
    keyPrefix,
    isQaMode,
    isFilterByFlatsEnabled,
    realtyUserId,
    isCountrysideBuildersBannerAvailable,
    isNewSimplifiedTGBEnabled,
    puidsByFilter,
  } = options;

  let positionOffset = options.positionOffset || 0;

  return offers.reduce((acc: React.ReactNode[], offer: IOffer, index) => {
    if (!hiddenOffers.includes(offer.id) && !hiddenSuggestions.includes(offer.id)) {
      const isOfferExtended = offer.extensionLabels && offer.extensionLabels.length;
      const position = (currentPageNumber - 1) * perPage + index;
      // порядок для события считаем начиная с единицы
      let trackingPosition = position + 1;

      // у добивок собственное начало отсчета
      trackingPosition = isOfferExtended ? trackingPosition - (extendedOffersOffset || 0) : trackingPosition;

      const nativeTgbPosition = trackingPosition + 1;

      const additionalPuids = {
        ...getAdditionalAdFoxPuids(jsonQuery),
        puid6: priceRange || '',
        ...(isQaMode ? { puid32: 'test' } : {}),
      };

      positionOffset += 1;

      const generateMortgageBanner = () =>
        addAdFoxBanner({
          type: isNewSimplifiedTGBEnabled ? 'mortgage_in_listing_new' : 'mortgage_in_listing',
          key: `${keyPrefix}-mortgage_in_listing-${offer.id}`,
          makeRequest,
          offer,
          isAgent,
          realtyUserId,
          position: nativeTgbPosition,
          shouldSendAdsEvent: isNewSimplifiedTGBEnabled && (shouldSendAdsEvent(jsonQuery) || !isFilterByFlatsEnabled),
          isNew: isNewSimplifiedTGBEnabled,
          width: '100%',
          minHeight: '275px',
        });

      const generateTgbBanner = () =>
        addAdFoxBanner({
          type: isNewSimplifiedTGBEnabled ? 'tgb_in_offers_list_new' : 'tgb_in_offers_list',
          key: `${keyPrefix}-tgb_in_offers_list-${offer.id}`,
          makeRequest,
          offer,
          position: nativeTgbPosition,
          locationId,
          isAgent,
          additionalPuids: {
            ...additionalPuids,
            ...getRailwayAndHighwayPuids(jsonQuery),
          },
          realtyUserId,
          shouldSendAdsEvent: shouldSendAdsEvent(jsonQuery) || !isFilterByFlatsEnabled,
          isNew: isNewSimplifiedTGBEnabled,
          width: '100%',
          minHeight: '275px',
        });
      const generateParallaxBanner = () =>
        addAdFoxBanner({
          type: 'parallax',
          key: `${keyPrefix}-parallax-${offer.id}`,
          makeRequest,
          offer,
          locationId,
          isAgent,
          additionalPuids: {
            ...additionalPuids,
            ...getRailwayAndHighwayPuids(jsonQuery),
            ...puidsByFilter,
          },
          isNew: true,
          ignoreOfferPuids: true,
          shouldSendAdsEvent: shouldSendAdsEvent(jsonQuery) || !isFilterByFlatsEnabled,
          width: '100%',
          minHeight: '395px',
        });

      acc.push(
        offerCardCreator(
          offer,
          trackingPosition,
          suggestionFragment.isSimilar ? index + suggestionFragment.startIndex : index,
          currentPageNumber,
          keyPrefix,
        ),
      );

      if (!isPrintEnabled) {
        if (positionOffset === 15) {
          acc.push(generateMortgageBanner());
        }

        if (positionOffset === 5 || (positionOffset === 25 && offers.length > 25)) {
          acc.push(generateTgbBanner());
        }

        if (showCountrysideTrap && villagesLink && position === 1) {
          acc.push(<CountrysideTrapBanner key={`countryside_banner_${position}`} villagesLink={villagesLink} />);
        }

        if (offers.length !== positionOffset && [2, 32, 62].includes(position)) {
          acc.push(
            dealTypeFromJsonQuery(jsonQuery) === EDealType.RentDaily ? (
              <OneTwoTripBannerContainer />
            ) : (
              <BannerConatiner
                key={`banner_${position}`}
                isAgent={isAgent}
                options={options}
                position={position}
                locationId={locationId}
                isCountrysideBuildersBannerAvailable={isCountrysideBuildersBannerAvailable}
              />
            ),
          );
        }

        if (positionOffset === 10 || positionOffset === 20) {
          acc.push(generateParallaxBanner());
        }
      }
    }

    return acc;
  }, []);
};

export const getOnFavoriteChange =
  (
    offer: IOffer,
    jsonQuery: IJsonQuery,
    position: number,
    onFavoriteChangedHandler: (offer: IOffer, isFavorite: boolean) => void,
  ) =>
  (isFavorite: boolean) => {
    const trackingLabel = offer.gaLabel;

    if (isFavorite) {
      const kpAdvantages = isSaleFromSuburbanBuilder(offer) ? getOfferKpAdvantages(offer) : undefined;

      trackFavoriteAdded(jsonQuery, { offerId: offer.id, position, kpAdvantages }, trackingLabel || undefined);
    } else {
      trackFavoriteRemoved(jsonQuery, { offerId: offer.id, position }, trackingLabel || undefined);
    }

    onFavoriteChangedHandler(offer, isFavorite);
  };

export interface IOfferCardCreatorProps extends TOffersProps {
  offersQty: number;
  queryString: string;
  priceRange?: string;
  breadcrumbs: IBreadcrumb[];
  isAgent?: boolean;
}

interface IGetOfferCardCreator {
  props: IOfferCardCreatorProps;
  isSimilar?: boolean;
  isSimilarFromML?: boolean;
  pageNum?: number;
  isExtended?: boolean;
}

export const getOfferCardCreator =
  ({ props, isSimilar, isSimilarFromML, pageNum }: IGetOfferCardCreator) =>
  // eslint-disable-next-line react/display-name
  (offer: IOffer, position: number, index: number, currentPageNumber: number, keyPrefix: string): React.ReactNode => {
    const {
      breadcrumbs,
      currentPath,
      onFavoriteChanged,
      makeRequest,
      isPrintEnabled,
      jsonQuery,
      onUserInfoPopupClose,
      onUserInfoPopupOpen,
      hideOfferAction,
      qsToUris,
      suggestionDistancesSeoText,
      user,
      offersQty,
      mlRankingGuid,
      mlRankingModelVersion,
      queryString,
      priceRange,
      hideOffer: { hideOfferState, hidingOffer },
      maxAuctionBet,
      maxAuctionService,
      isAgent,
      isQaMode,
      trackingData,
      auctionBanks,
      villagePromotionLabel,
      addToComparison,
      deleteFromComparison,
      openChat,
      openNotAvailablePopup,
    } = props;

    const { id, bargainTerms } = offer;
    const withAuction = Boolean(
      !!auctionBanks && !!offer.user && !!offer.user.cianUserId && auctionBanks.includes(+offer.user.cianUserId),
    );
    const additionalPuids = {
      ...getAdditionalAdFoxPuids(jsonQuery),
      puid6: priceRange || '',
      puid32: isQaMode ? 'test' : '',
    };

    const onPopupClose = () => onUserInfoPopupClose(offer.id);
    const onPopupOpen = () => onUserInfoPopupOpen(offer.id);
    const onHideOffer =
      hideOfferState === EHideOfferState.FETCHING || hideOfferState === EHideOfferState.ANIMATING
        ? () => {}
        : () => {
            trackHiddenObjects({ action: 'Click_icon', offer, jsonQuery });
            hideOfferAction({ offer, isSimilar: !!isSimilar });
          };

    const mortgageBanner =
      bargainTerms && bargainTerms.mortgageAllowed && !isPrintEnabled
        ? addAdFoxBanner({
            type: props.featureToggle.isNewMortgageBannerEnabled ? 'mortgage_banner_new' : 'mortgage_banner',
            key: `${keyPrefix}-mortgage_banner-${id}`,
            makeRequest,
            offer,
            additionalPuids,
            isAgent,
            isNew: props.featureToggle.isNewMortgageBannerEnabled,
            width: '100%',
            minHeight: '20px',
          })
        : null;

    const onPageJumped = (label: string) =>
      trackPageJumped(offer, {
        user,
        jsonQuery,
        position,
        isSimilarFromML,
        label,
        pageNum,
        breadcrumbs,
        queryString,
        offersQty,
        mlRankingGuid,
        mlRankingModelVersion,
      });

    const offerCardProps = prepareOfferCardComponentsProps(props, {
      mortgageBanner,
      onPageJumped,
      onPopupClose,
      onPopupOpen,
      onHideOffer,
      onFavoriteChange: getOnFavoriteChange(offer, jsonQuery, position, onFavoriteChanged),
      trackPhoneOpened: (isFromBrandBlock?: boolean) =>
        trackPhoneOpened(offer, position, jsonQuery, user, offersQty, queryString, isSimilar, isFromBrandBlock),
      offer,
      jsonQuery,
      position,
      addToComparison,
      deleteFromComparison,
    });

    return (
      <OfferContainer
        key={`${keyPrefix}-${id}`}
        index={index}
        currentPath={currentPath}
        suggestionDistancesSeoText={suggestionDistancesSeoText}
        position={position}
        trackingData={trackingData}
        queryString={queryString}
        offer={offer}
        jsonQuery={jsonQuery}
        qsToUris={qsToUris}
        user={user}
        mortgageBanner={mortgageBanner}
        excludedUtilitiesTermsRegions={offerCardProps.excludedUtilitiesTermsRegions}
        maxAuctionBet={maxAuctionBet}
        maxAuctionService={maxAuctionService}
        isHideOfferInProgress={
          (Boolean(hidingOffer && hidingOffer.offer.id === offer.id) && hideOfferState === EHideOfferState.FETCHING) ||
          (Boolean(hidingOffer && hidingOffer.offer.id === offer.id) && hideOfferState === EHideOfferState.ANIMATING)
        }
        api={{
          onAgentRatingChanged: offerCardProps.userInfo.onAgentRatingChanged,
          onPageJumped,
          onHideOffer: offerCardProps.buttons.onHideOffer,
          isPopupsOpened: props.isPopupsOpened,
          onFavoritesChange: offerCardProps.offerCard.onFavoriteChange,
          sendComplaintFeedback: offerCardProps.complaintsHelpers.sendComplaintFeedback,
          onComplaintSent: offerCardProps.reportPopup.onComplaintSent,
          addToComparison: offerCardProps.offerComparison.addToComparison,
          deleteFromComparison: offerCardProps.offerComparison.deleteFromComparison,
          openChat,
          openNotAvailablePopup,
        }}
        isBot={offerCardProps.pessimisation.isBot}
        tracking={offerCardProps.tracking}
        featureToggle={offerCardProps.featureToggle}
        withAuction={withAuction}
        isDealRentDisablePhonesExperimentActive={Boolean(
          offer.offerInDeal && isDealRentDisablePhonesExperimentActive(props.abUseExperiments),
        )}
        isRosreestrCheckedLabelAvailable={isRosreestrCheckedLabelAvailable(offer)}
        isNewbuildingBookedFromDeveloper={isNewbuildingBookedFromDeveloper(offer)}
        villagePromotionLabel={villagePromotionLabel}
      />
    );
  };

export function getAdditionalAdFoxPuids(jsonQuery: IJsonQuery): { [key: string]: string } {
  const puid9 = (
    (jsonQuery.geo &&
      jsonQuery.geo.value
        .filter(geo => geo.type === 'underground')
        .map((underground: IJsonQueryUnderground) => underground.id)) ||
    []
  ).join(':');

  return {
    puid9,
  };
}

export function getRailwayAndHighwayPuids(jsonQuery: IJsonQuery): { [key: string]: string } {
  return jsonQuery.geo && jsonQuery.geo.value
    ? jsonQuery.geo.value.reduce(
        (acc, next) => {
          if (next.type === 'railway' && jsonQuery._type.includes('suburban')) {
            acc.puid34 = acc.puid34 ? `${acc.puid34}:${next.id}` : String(next.id);
          }

          if (next.type === 'highway' && jsonQuery._type.includes('suburban')) {
            acc.puid35 = acc.puid35 ? `${acc.puid35}:${next.id}` : String(next.id);
          }

          return acc;
        },
        { puid34: '', puid35: '' },
      )
    : {};
}

export function getOfferPrice(offer: IOffer) {
  let price = offer.minPriceTotalRur || offer.bargainTerms.priceRur;

  if (offer.bargainTerms.utilitiesTerms && !offer.bargainTerms.utilitiesTerms.includedInPrice) {
    price += offer.bargainTerms.utilitiesTerms.price;
  }

  return price;
}
